import React from 'react';
import {Conversion} from '../../domain/types';
import ConversionRow, {ConversionRowActions} from './ConversionRow';
import {SearchIcon, WandIcon, XIcon} from '../Icons';

interface ConversionsTableProps extends ConversionRowActions {
  conversions: Conversion[];
  isLoading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  /** Nothing is created on this page — the empty state hands the user back to the workspace. */
  onGoToWorkspace: () => void;
  downloadingId: string | null;
  /**
   * Conversions made before the backend recorded a media reference, mapped to why each one cannot
   * be run again (`null` when it can). Being in the map at all is what puts the note on the row.
   */
  mediaNotices: Map<string, string | null>;
  regeneratingId: string | null;
}

const HEADINGS = [
  {label: 'File', span: 'md:col-span-4'},
  {label: 'Word list', span: 'md:col-span-2'},
  {label: 'Censored', span: 'md:col-span-1'},
  {label: 'Cuts', span: 'md:col-span-1'},
  {label: 'Created', span: 'md:col-span-2'},
  {label: '', span: 'md:col-span-2'},
];

const ConversionsTable: React.FC<ConversionsTableProps> = ({
  conversions,
  isLoading,
  search,
  onSearchChange,
  onGoToWorkspace,
  downloadingId,
  mediaNotices,
  regeneratingId,
  ...rowActions
}) => {
  const hasSearch = search.trim().length > 0;

  return (
    <section className="rounded-xl border border-gray-800 bg-gray-900/60 overflow-hidden">
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-800">
        <h2 className="text-sm font-semibold text-gray-100">
          Conversions
          {conversions.length > 0 && (
            <span className="ml-2 font-normal text-gray-500">{conversions.length}</span>
          )}
        </h2>

        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600" />
          <input
            type="search"
            value={search}
            onChange={event => onSearchChange(event.target.value)}
            placeholder="Search by filename…"
            aria-label="Search conversions by filename"
            className="w-56 rounded-lg border border-gray-800 bg-gray-950/60 py-1.5 pl-8 pr-7 text-xs
             text-gray-200 placeholder-gray-600 focus:border-indigo-500 focus:outline-none
             focus:ring-1 focus:ring-indigo-500 [&::-webkit-search-cancel-button]:hidden"
          />
          {hasSearch && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-300 cursor-pointer"
            >
              <XIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </header>

      {conversions.length > 0 && (
        <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-2 border-b border-gray-800
         bg-gray-950/40 text-[11px] font-medium uppercase tracking-wide text-gray-500">
          {HEADINGS.map(heading => (
            <div key={heading.label || 'actions'} className={`col-span-12 ${heading.span}`}>
              {heading.label}
            </div>
          ))}
        </div>
      )}

      {isLoading && conversions.length === 0 && (
        <div className="px-4 py-10 text-center text-sm text-gray-500">Loading conversions…</div>
      )}

      {!isLoading && conversions.length === 0 && !hasSearch && (
        <div className="flex flex-col items-center gap-1 px-4 py-12 text-center">
          <p className="text-sm font-medium text-gray-200">No conversions yet</p>
          <p className="max-w-md text-sm text-gray-500">
            Transcribe an audio file in the workspace, then convert it against a word list to get a
            Final Cut Pro XML.
          </p>
          <button
            type="button"
            onClick={onGoToWorkspace}
            className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5
             text-xs font-medium text-white hover:bg-indigo-500 transition-colors cursor-pointer"
          >
            <WandIcon className="w-3.5 h-3.5" />
            Go to the workspace
          </button>
        </div>
      )}

      {!isLoading && conversions.length === 0 && hasSearch && (
        <div className="px-4 py-12 text-center">
          <p className="text-sm text-gray-400">No conversions match “{search.trim()}”.</p>
          <button
            type="button"
            onClick={() => onSearchChange('')}
            className="mt-2 text-xs text-indigo-400 hover:text-indigo-300 cursor-pointer"
          >
            Clear search
          </button>
        </div>
      )}

      {conversions.map(conversion => (
        <ConversionRow
          key={conversion.id}
          conversion={conversion}
          isDownloading={downloadingId === conversion.id}
          isRegenerating={regeneratingId === conversion.id}
          mediaNotice={mediaNotices.has(conversion.id)
            ? {blockedReason: mediaNotices.get(conversion.id) ?? null}
            : null}
          {...rowActions}
        />
      ))}
    </section>
  );
};

export default ConversionsTable;
